import { Component } from 'preact';
import { TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD } from '../lib/medievalTheme';
import { fetchReplaySummary } from '../utils/game/replay/replayApi';
import { resolveAvatarUrl } from '../utils/arena/avatarUtils';
import AvatarImage from './ui/AvatarImage';
import { matchModeLabel } from '../utils/arena/gameModes';
import { IconCrown } from './ui/icons';
import MatchupBackdrop from './MatchupBackdrop';
import { cn } from '../lib/utils';
import { t } from '../localization/t';

// The replay attachment in a DM: a scoreboard for one finished match.
//
// This IS the chat bubble, not something drawn inside one. renderMessage
// hands replay messages straight here without its usual wrapper, so the card
// is the only surface with a background, a radius and a shadow. Nesting a
// panel inside a card inside a bubble put three rounded rectangles on screen
// for one piece of content.
//
// Everything it draws is snapshotted into the message metadata at send time
// by ChatView.handleSendReplay. It cannot be re-derived on the recipient's
// side: match_history is one row per participant, so the sender's label, the
// mode and both Avatar ids live on rows the recipient cannot read.
//
// A card posted to the Tavern also carries `link`, the match's public replay
// token the server pinned on at post time. It rides every read the card and
// the viewer make, since a Tavern reader has no access of their own.

const LABEL_SHADOW = '0 1px 2px rgba(0,0,0,0.95), 0 0 6px rgba(0,0,0,0.85), 0 0 14px rgba(0,0,0,0.6)';

const WINNER_GOLD = '#d4a843';
const LOSER_TEXT = '#8a8279';

/**
 * Both seats of a shared match, sender first, or null when the message
 * predates the two-player metadata (those cards fall back to a one-line
 * summary rather than inventing an opponent).
 */
export function normalizeReplayPlayers(meta) {
  const players = meta?.players;
  if (!Array.isArray(players) || players.length !== 2) return null;
  if (!players.every((p) => p && typeof p.name === 'string' && p.name)) return null;
  return players.map((p) => ({
    name: p.name,
    // Two different pictures. `avatarId` is the player's PROFILE picture,
    // shown in the round portrait; `cardId` is the Avatar card their deck
    // ran, which is what the splash behind the card is cut from. Cards sent
    // before the split carry only `avatarId`, and it held the Avatar card.
    avatarId: p.avatarId || null,
    cardId: p.cardId || null,
    won: !!p.won,
  }));
}

/**
 * A match where one player sat in both seats (practice against yourself).
 * Cards sent since the archive started flagging it carry `solo: true`
 * outright. Older ones are recognised by both seats carrying the same name:
 * the one match_history row names the pilot as their own opponent, and
 * exactly one seat is flagged as the winner, which would otherwise render
 * as the same person beating themselves.
 */
export function isSoloMatch(players, meta) {
  if (meta?.solo === true) return true;
  if (!players) return false;
  // Two winners is the third tell: the archive records a solo pilot as
  // winner AND loser, and cards the viewer sent before it flagged solo
  // carry that as `won: true` on both seats. No two-player match can.
  return players[0].name === players[1].name || (players[0].won && players[1].won);
}

/**
 * A solo card's seats with a usable verdict: exactly one seat flagged, or
 * neither. Both flagged means the result is unknown (see isSoloMatch), and
 * drawing two crowns would claim both decks won.
 */
function soloVerdict(players) {
  if (!players || !(players[0].won && players[1].won)) return players;
  return players.map((p) => ({ ...p, won: false }));
}

/**
 * The two decks a solo match was played between, from the Avatar cards
 * snapshotted off the archived bundle at send time. Null for a card sent
 * before that snapshot existed, or when the bundle had no Avatar to read.
 *
 * The verdict comes from the seats in `players`: the pilot's `won` is
 * seat one's, so the Avatar in seat one wins when they did and seat two
 * wins otherwise. Without `players` (or when neither seat is flagged) no
 * Avatar is marked, and the card draws the matchup with no verdict.
 */
export function matchAvatarSeats(meta, players = null) {
  const a = meta?.matchAvatars;
  if (!a?.p1?.cardId || !a?.p2?.cardId) return null;
  return ['p1', 'p2'].map((seat, i) => ({
    name: a[seat].name || t('Avatar'),
    // A solo card has no people to show: both portraits ARE the decks.
    avatarId: a[seat].cardId,
    cardId: a[seat].cardId,
    won: !!players?.[i]?.won,
  }));
}

/**
 * Fallback labels for a solo match's two seats when the bundle snapshot is
 * missing: the pilot's name with a seat suffix, and no portrait. The only
 * avatar id on hand is the pilot's PROFILE avatar, which is a display
 * preference, not the Avatar card either deck ran; drawing it in a seat
 * would claim a matchup the card cannot know.
 */
export function soloSeats(players) {
  return players.map((p, i) => ({ ...p, avatarId: null, cardId: null, name: t('{name} (Seat {n})', { name: p.name, n: i + 1 }) }));
}

/**
 * The card's metadata for a match_history row: the viewer sits in seat one,
 * their opponent in seat two. Shared by the DM composer (which snapshots the
 * result into the message, since the recipient cannot read the sender's row)
 * and by the Past Matches grid (which renders the same card from the row it
 * already holds).
 */
export function replayMetaFromHistory(m, profile) {
  return {
    roomCode: m.roomCode,
    opponentName: m.opponentName || 'Opponent',
    won: !!m.won,
    playedAt: m.playedAt || null,
    customName: m.customName || null,
    mode: m.mode || null,
    // A Tavern-posted match on someone else's profile: the public token is
    // the viewer's only credential, so it rides the card like a Tavern post.
    ...(m.replayLink ? { link: m.replayLink } : {}),
    players: [
      {
        name: profile?.name || 'You',
        avatarId: profile?.profileAvatar || null,
        // match_history.avatar_id is the unique_id of the Avatar CARD the
        // deck ran, not a profile picture.
        cardId: m.avatarId || null,
        won: !!m.won,
      },
      {
        name: m.opponentName || 'Opponent',
        avatarId: m.opponentProfileAvatar || null,
        cardId: m.opponentAvatarId || null,
        won: !m.won,
      },
    ],
  };
}

/**
 * The card's metadata for a replay the sender has open in the viewer. The
 * viewer's seat labels are NOT the players: on a solo replay they are the
 * two Avatars, so building the seats from them named the pilot's second
 * deck as an opponent, and the card read as a two-player Sandbox match.
 *
 * Solo is read off the archive's own flag and snapshotted as `solo: true`,
 * with the pilot in both seats, as the DM picker's history-row path does.
 * The archive records the pilot as both winner and loser, so seat one's
 * verdict comes from the pilot's match_history row when the sender still
 * holds it; without one, neither Avatar is marked.
 */
export function replayMetaFromView({ view, names, profile, historyRow = null }) {
  const meta = view?.meta || {};
  const seats = meta.seats || {};
  const avatars = meta.avatars || null;
  const base = {
    roomCode: meta.roomCode || null,
    playedAt: meta.endedAt || null,
    customName: historyRow?.customName || null,
    mode: meta.mode || null,
    matchAvatars: avatars,
  };
  if (meta.solo) {
    const pilot = { name: profile?.name || 'You', avatarId: profile?.profileAvatar || null, cardId: null };
    const p1Won = typeof historyRow?.won === 'boolean' ? historyRow.won : null;
    return {
      ...base,
      solo: true,
      opponentName: pilot.name,
      won: p1Won === true,
      players: [
        { ...pilot, cardId: avatars?.p1?.cardId || null, won: p1Won === true },
        { ...pilot, cardId: avatars?.p2?.cardId || null, won: p1Won === false },
      ],
    };
  }
  const ownSeat = view?.ownSeat || 'p1';
  const otherSeat = ownSeat === 'p1' ? 'p2' : 'p1';
  const seatPlayer = (seat) => ({
    name: seat === ownSeat ? (profile?.name || 'You') : names?.[seat],
    avatarId: seat === ownSeat ? profile?.profileAvatar || null : null,
    cardId: avatars?.[seat]?.cardId || null,
    won: meta.winnerId === seats[seat],
  });
  return {
    ...base,
    opponentName: names?.[otherSeat],
    won: meta.winnerId === profile?.id,
    players: [seatPlayer(ownSeat), seatPlayer(otherSeat)],
  };
}

// Who played the match, small in the header. A solo card's two portraits are
// Avatar CARDS, not people, so without this the pilot is nowhere on it.
function Pilot({ player, sorceryCards }) {
  const url = resolveAvatarUrl({ profileAvatar: player.avatarId }, sorceryCards);
  return (
    <span className="shrink-0 flex items-center gap-1 min-w-0">
      <span
        className="shrink-0 overflow-hidden rounded-full"
        style={{
          width: '1.125cqw',
          height: '1.125cqw',
          background: '#1a1611',
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1)',
        }}
      >
        {url ? (
          <AvatarImage src={url} className="w-full h-full" />
        ) : null}
      </span>
      <span
        className="truncate text-[0.6875cqw] font-semibold"
        style={{ color: TEXT_PRIMARY, textShadow: LABEL_SHADOW }}
      >
        {player.name}
      </span>
    </span>
  );
}

// The portrait fills whatever vertical room the card has left after the
// title and the footer, capped so a wide chat card does not blow it up.
// Sizing it in cqw instead would pin it to the canvas, and the same portrait
// would then overflow a Past Matches tile (a third of a panel wide) while
// swimming in a chat bubble.
const SEAT_MAX = '4.5cqw';

function Seat({ player, sorceryCards, verdict = true }) {
  const url = resolveAvatarUrl({ profileAvatar: player.avatarId }, sorceryCards);
  const won = verdict && player.won;
  const color = !verdict ? TEXT_PRIMARY : won ? WINNER_GOLD : LOSER_TEXT;
  return (
    <span className="flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 h-full">
      <span
        className="relative flex-1 min-h-0 aspect-square overflow-hidden rounded-full"
        style={{
          maxHeight: SEAT_MAX,
          background: '#1a1611',
          // Depth in one stack, outside in: the ring, a bloom that only the
          // winner gets, a cast shadow that lifts the portrait off the art,
          // then an inner top highlight and bottom shade so the disc reads
          // as a domed medallion rather than a flat hole cut in the card.
          boxShadow: [
            won ? `0 0 0 0.125cqw ${WINNER_GOLD}` : '0 0 0 0.09375cqw rgba(226,214,190,0.5)',
            won ? '0 0 0.875cqw rgba(212,168,67,0.55)' : null,
            '0 0.1875cqw 0.4375cqw rgba(0,0,0,0.8)',
            'inset 0 0.0625cqw 0 rgba(255,255,255,0.28)',
            'inset 0 -0.1875cqw 0.375cqw rgba(0,0,0,0.55)',
          ].filter(Boolean).join(', '),
        }}
      >
        {url ? (
          <AvatarImage
            src={url}
            className="w-full h-full"
            style={{ filter: !verdict || won ? 'none' : 'saturate(0.5)' }}
          />
        ) : (
          <span
            className="absolute inset-0 flex items-center justify-center text-[1.375cqw] font-semibold"
            style={{ color }}
          >
            {player.name.slice(0, 1)}
          </span>
        )}
      </span>

      <span className="flex shrink-0 items-center gap-0.5 max-w-full min-w-0">
        {won ? <IconCrown size={12} style={{ color: WINNER_GOLD, flexShrink: 0 }} /> : null}
        <span className="truncate text-[0.875cqw] font-semibold" style={{ color, textShadow: LABEL_SHADOW }}>{player.name}</span>
      </span>

      {verdict ? (
        <span
          className="shrink-0 text-[0.6875cqw] font-semibold tabular-nums"
          style={{ color: won ? WINNER_GOLD : TEXT_BODY, textShadow: LABEL_SHADOW }}
        >
          {won ? t('Won') : t('Lost')}
        </span>
      ) : null}
    </span>
  );
}

/**
 * The card's own loading state. Wears the same plate and the same geometry,
 * so the skeleton and the card it becomes occupy the identical box.
 *
 * Deliberately NOT the `.vh-mode-tile` class: that plate is a control, and a
 * skeleton that lifts and glows under the pointer invites a click on nothing.
 */
export function ReplayCardSkeleton({ className }) {
  const bar = (w, h) => (
    <span className="skeleton-block" style={{ width: w, height: h }} />
  );
  return (
    <div
      className={cn('relative overflow-hidden', className)}
      aria-hidden
      style={{
        aspectRatio: '16 / 9',
        borderRadius: 'var(--radius-xl)',
        background: 'linear-gradient(180deg, #16120d 0%, #0f0c08 100%)',
        boxShadow: 'inset 0 0.0625cqw 0 rgba(255,230,170,0.05), 0 0 0 0.0625cqw rgba(212,168,67,0.14), 0 0.5cqw 1.25cqw rgba(0,0,0,0.5)',
      }}
    >
      <span className="absolute inset-0 flex flex-col gap-1" style={{ padding: '0.625cqw 0.75cqw' }}>
        <span className="flex items-start gap-1.5 w-full">
          <span className="flex flex-col gap-1 min-w-0 flex-1">
            {bar('55%', '0.875cqw')}
            {bar('30%', '0.5625cqw')}
          </span>
          {bar('2.75cqw', '0.6875cqw')}
        </span>

        <span className="flex flex-1 min-h-0 items-center justify-center gap-1.5">
          {[0, 1].map((i) => (
            <span key={i} className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full">
              <span
                className="skeleton-block skeleton-circle flex-1 min-h-0 aspect-square"
                style={{ maxHeight: SEAT_MAX }}
              />
              {bar('60%', '0.875cqw')}
              {bar('30%', '0.6875cqw')}
            </span>
          ))}
        </span>

        <span className="flex items-center gap-1 w-full">
          {bar('35%', '0.6875cqw')}
          <span className="ml-auto">{bar('3.25cqw', '0.625cqw')}</span>
        </span>
      </span>
    </div>
  );
}

export default class ReplayShareCard extends Component {
  // The card holds the skeleton until EVERY piece it draws is in hand: the
  // matchup summary (solo cards only) and all four images. Letting each land
  // on its own arrival gave a card that assembled itself in stages, which
  // reads as broken rather than as loading.
  state = { fetchedAvatars: null, imagesReady: false };

  componentDidMount() {
    this.loadAvatars();
    this.preloadArt();
  }

  componentDidUpdate() {
    // The summary lands late on a solo card and names two more images.
    this.preloadArt();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  // Cards sent before the summary snapshot existed carry no matchAvatars.
  // Anyone who can see the card can watch the replay, so the recipient reads
  // the same summary the sender would have snapshotted. Best-effort: on
  // failure the seat-suffix fallback stands.
  async loadAvatars() {
    if (!this.awaitingSummary()) return;
    const summary = await fetchReplaySummary(this.props.meta.roomCode, this.props.meta.link || null).catch(() => null);
    if (this.unmounted) return;
    // `fetchedAvatars: false` marks a settled-but-empty fetch, so the seat
    // suffix fallback can render instead of the card waiting forever.
    this.setState({ fetchedAvatars: summary?.avatars || false });
  }

  /** Whether the matchup still has to come from the server. */
  awaitingSummary() {
    const { meta } = this.props;
    if (this.state.fetchedAvatars !== null) return false;
    if (meta?.matchAvatars || !meta?.roomCode) return false;
    return isSoloMatch(normalizeReplayPlayers(meta), meta);
  }

  /** Every image the finished card draws, deduped, in no particular order. */
  artUrls() {
    const { sorceryCards } = this.props;
    const seats = this.seats();
    const players = normalizeReplayPlayers(this.props.meta);
    const ids = [
      ...(seats || []).flatMap((s) => [s.cardId, s.avatarId]),
      // The pilot chip on a solo card.
      ...(players ? [players[0].avatarId] : []),
    ];
    return [...new Set(
      ids.filter(Boolean)
        .map((id) => resolveAvatarUrl({ profileAvatar: id }, sorceryCards))
        .filter(Boolean),
    )];
  }

  // Warm the browser cache for the whole set, then reveal in one go. Every
  // <img> the card renders is then a cache hit and paints on the same frame.
  preloadArt() {
    if (this.awaitingSummary()) return;
    const urls = this.artUrls();
    const key = urls.join('|');
    if (this._artKey === key) return;
    this._artKey = key;
    if (!urls.length) {
      if (!this.state.imagesReady) this.setState({ imagesReady: true });
      return;
    }
    let pending = urls.length;
    // Errors settle too: a missing portrait falls back to a monogram, which
    // is a finished card, not a reason to shimmer forever.
    const settle = () => {
      if (--pending > 0 || this.unmounted || this._artKey !== key) return;
      this.setState({ imagesReady: true });
    };
    for (const url of urls) {
      const img = new Image();
      img.onload = settle;
      img.onerror = settle;
      img.src = url;
    }
  }

  /** Both seats as the card will draw them, or null pre-metadata. */
  seats() {
    const { meta } = this.props;
    const players = normalizeReplayPlayers(meta);
    if (!players) return null;
    if (!isSoloMatch(players, meta)) return players;
    const judged = soloVerdict(players);
    return matchAvatarSeats({ matchAvatars: meta?.matchAvatars || this.state.fetchedAvatars }, judged)
      || soloSeats(judged);
  }

  render() {
    // `canWatch` is not "does a replay exist": nothing lists a match without
    // one. It is the momentary block while the row underneath is renamed.
    const { meta, sorceryCards, onWatch, canWatch = true, className } = this.props;
    // One gate for the whole card: nothing renders until the matchup and all
    // of its art are in hand.
    if (this.awaitingSummary() || !this.state.imagesReady) {
      return <ReplayCardSkeleton className={className} />;
    }
    const players = normalizeReplayPlayers(meta);
    const solo = isSoloMatch(players, meta);
    // Both seats always render. Solo swaps the two people (one account, one
    // name, twice) for the two Avatars the match was actually played between.
    // The verdict then belongs to an Avatar, not a person: seat one's `won`
    // is the pilot's, so it marks which deck took the match. A solo card
    // whose result is unknown flags neither seat and shows no verdict.
    const seats = this.seats();
    const verdict = !solo || !!seats?.some((s) => s.won);
    // Every match has a mode, so the corner always carries one. Rows from
    // before solo had its own bucket say 'sandbox', so who sat in the seats
    // decides, not the stored mode.
    const mode = matchModeLabel(meta?.mode);
    const modeLabel = solo ? t('Solo') : (mode ? t(mode) : t('Match'));
    const playedAt = meta?.playedAt ? new Date(meta.playedAt).toLocaleDateString() : null;
    // A solo match has no opponent to be "vs": the seats below name the two
    // Avatars, so the title only says what kind of match it was.
    const title = meta?.customName || (solo ? t('Solo match') : t('vs {name}', { name: meta?.opponentName || t('Opponent') }));
    // The splash is cut from the Avatar CARDS the two decks ran. Cards sent
    // before profile picture and Avatar card were separate fields carry only
    // the one id, and back then it held the Avatar card.
    const seatArt = (seat) => resolveAvatarUrl(
      { profileAvatar: seat.cardId || seat.avatarId }, sorceryCards,
    );
    const backdrop = seats ? {
      localName: seats[0].name,
      remoteName: seats[1].name,
      localAvatarUrl: seatArt(seats[0]),
      remoteAvatarUrl: seatArt(seats[1]),
    } : null;

    return (
      <button
        type="button"
        // The mode tile's plate: same lift on hover, same press, since this
        // is the same kind of object: one match you can open.
        className={cn('vh-mode-tile replay-card-in', className)}
        disabled={!canWatch}
        title={canWatch ? t('Watch this replay') : undefined}
        onClick={canWatch ? () => onWatch?.(meta?.roomCode, { opponentName: meta?.opponentName, ...(meta?.link ? { link: meta.link } : {}) }) : undefined}
      >
        <div className="vh-mode-tile-art" aria-hidden>
          <MatchupBackdrop
            matchup={backdrop}
            won={!!seats?.[0]?.won}
            neutral={!verdict || !players}
            headings={false}
            animated={false}
            scrim={0.5}
            edgeScrim={1}
            creaseStroke={0.5}
          />
          <div className="vh-mode-tile-ember" />
          <div className="vh-mode-tile-sheen" />
        </div>

        <span
          className="absolute inset-0 flex flex-col gap-1 text-left"
          style={{ padding: '0.625cqw 0.75cqw', zIndex: 4 }}
        >
          <span className="flex items-start gap-1.5 w-full min-w-0">
            <span className="min-w-0 flex-1 flex flex-col">
              <span
                className="truncate text-[0.875cqw] font-semibold"
                style={{ color: TEXT_PRIMARY, textShadow: LABEL_SHADOW }}
              >
                {title}
              </span>
              {/* Solo only. The two portraits below are DECKS, not people, so
                  without this the pilot is nowhere on the card. On a
                  two-player card both names are already under the portraits. */}
              {solo && players ? <Pilot player={players[0]} sorceryCards={sorceryCards} /> : null}
            </span>
            <span className="shrink-0 text-[0.6875cqw] font-semibold" style={{ color: ACCENT_GOLD, textShadow: LABEL_SHADOW }}>
              {modeLabel}
            </span>
          </span>

          {players ? (
            <span className="flex flex-1 min-h-0 items-center justify-center gap-1.5 w-full min-w-0">
              <Seat player={seats[0]} sorceryCards={sorceryCards} verdict={verdict} />
              <span className="shrink-0 text-[0.625cqw] italic" style={{ color: TEXT_BODY, textShadow: LABEL_SHADOW }}>{t('vs')}</span>
              <Seat player={seats[1]} sorceryCards={sorceryCards} verdict={verdict} />
            </span>
          ) : null}

          <span className="flex items-center gap-1 w-full">
            {/* Nudged right: a play triangle's visual mass sits left of its
                bounding box, so a centred one reads off-centre. */}
            <svg
              className="shrink-0"
              style={{
                width: '0.75cqw',
                height: '0.75cqw',
                marginLeft: '0.0625cqw',
                color: ACCENT_GOLD,
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.95))',
              }}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M8 5.5 19 12 8 18.5z" />
            </svg>
            <span className="text-[0.6875cqw] font-medium" style={{ color: TEXT_BODY, textShadow: LABEL_SHADOW }}>
              {solo ? t('Watch solo match') : t('Watch replay')}
            </span>
            {playedAt ? (
              <span className="ml-auto text-[0.625cqw] tabular-nums" style={{ color: TEXT_BODY, textShadow: LABEL_SHADOW }}>
                {playedAt}
              </span>
            ) : null}
          </span>
        </span>
      </button>
    );
  }
}
